/* GG29 hero: crossfades through the photos, starting after the first scroll/tap/key. Pauses on hover, keyboard focus, the pause button, when the
   hero is scrolled out of view or the tab is hidden, and never starts for people who turn off motion. */
if (!customElements.get('gg29-hero-media')) {
  customElements.define(
    'gg29-hero-media',
    class Gg29HeroMedia extends HTMLElement {
      connectedCallback() {
        if (this.dataset.rotate !== 'true') return;
        this.slides = [...this.querySelectorAll('.gg29-hero__slide')];
        if (this.slides.length < 2) return;
        this.index = 0;
        this.delay = (parseFloat(this.dataset.seconds) || 6) * 1000;
        this.button = this.querySelector('.gg29-hero__pause');
        this.holds = new Set(); // reasons it is paused right now
        this.tick = this.tick.bind(this);

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.holds.add('user');
        // Start after the visitor's first scroll, tap or key press. Browsers stop measuring page speed
        // (Largest Contentful Paint) at that point, so a later photo can't count as the page's "main image".
        this.holds.add('waiting');
        const start = () => {
          ['scroll', 'wheel', 'pointerdown', 'touchstart', 'keydown'].forEach((t) => window.removeEventListener(t, start, true));
          this.holds.delete('waiting');
          this.schedule();
        };
        ['scroll', 'wheel', 'pointerdown', 'touchstart', 'keydown'].forEach((t) => window.addEventListener(t, start, { passive: true, capture: true }));
        this.updateButton();
        this.button.addEventListener('click', () => {
          this.holds.has('user') ? this.holds.delete('user') : this.holds.add('user');
          this.updateButton();
          this.schedule();
        });
        const hold = (reason, on) => {
          on ? this.holds.add(reason) : this.holds.delete(reason);
          this.schedule();
        };
        this.addEventListener('mouseenter', () => hold('hover', true));
        this.addEventListener('mouseleave', () => hold('hover', false));
        this.addEventListener('focusin', () => hold('focus', true));
        this.addEventListener('focusout', () => hold('focus', false));
        this.onVisibility = () => hold('hidden', document.hidden);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.observer = new IntersectionObserver(([entry]) => hold('offscreen', !entry.isIntersecting));
        this.observer.observe(this);

        // In the theme editor, selecting an "Extra photo" block shows that photo and holds it there.
        this.onBlockSelect = (e) => {
          const i = this.slides.findIndex((s) => s.contains(e.target));
          if (i === -1) return;
          this.show(i);
          hold('editor', true);
        };
        this.onBlockDeselect = () => hold('editor', false);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

        this.schedule();
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        this.observer?.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibility);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      schedule() {
        clearTimeout(this.timer);
        if (this.holds.size === 0) this.timer = setTimeout(this.tick, this.delay);
      }

      tick() {
        this.show((this.index + 1) % this.slides.length);
        this.schedule();
      }

      show(i) {
        this.index = i;
        this.slides.forEach((slide, n) => {
          const active = n === i;
          slide.classList.toggle('is-active', active);
          slide.toggleAttribute('aria-hidden', !active);
          const chip = slide.querySelector('.gg29-hero__chip');
          if (chip) active ? chip.removeAttribute('tabindex') : chip.setAttribute('tabindex', '-1');
          // Start loading the next photo ahead of time.
          if (n === (i + 1) % this.slides.length) slide.querySelector('img')?.setAttribute('loading', 'eager');
        });
      }

      updateButton() {
        const paused = this.holds.has('user');
        this.button.classList.toggle('is-paused', paused);
        this.button.setAttribute('aria-label', paused ? this.button.dataset.labelPlay : this.button.dataset.labelPause);
      }
    }
  );
}
